import type { QueueItemMetadataMap } from '~/jet/models';
import { writable, type Writable, get } from 'svelte/store';

/**
 * Subscribable store to access metadata for items in the current playback queue
 * Accessible by subscribing to the store and getting the particular ID that you
 * needed.  Data is retained until the queue is cleared or replaced.
 *
 * Typical setup example:
 * ```
 * <script lang="ts">
 *   import { queueMetadata } from '~/stores/queue-metadata';
 *   import { getPlayback } from '~/utils/playback';
 *
 *   const { queue } = getPlayback();
 *
 *   $: nowPlayingItem = $queue.nowPlayingItem;
 *   $: nowPlayingItemId = nowPlayingItem?.item?.id;
 *
 *   $: nowPlayingMetadata = !!nowPlayingItemId && $queueMetadata?.get(nowPlayingItemId);
 * </script>
 * ```
 */
export const queueMetadata: Writable<QueueItemMetadataMap | null> = writable(
    new Map() as QueueItemMetadataMap,
);

/**
 * Completely resets the queue metadata store, wiping all stored content
 *
 * @method resetQueueMetadata
 * @returns
 */
export const resetQueueMetadata = (): void => {
    queueMetadata.set(new Map() as QueueItemMetadataMap);
};

/**
 * Sets a new metadata map when the queue changes
 *
 * @method setQueueMetadata
 * @param {QueueMetadataMap} queueMetadataMap map of queue items from API
 */
export const setQueueMetadata = (
    queueMetadataMap: QueueItemMetadataMap,
): void => {
    queueMetadata.set(new Map([...queueMetadataMap]));
};

/**
 * Updates the existing store data with additional items
 *
 * @method updateQueueMetadata
 * @param {QueueMetadataMap} queueMetadataMap map of queue items from API
 */
export const updateQueueMetadata = (
    queueMetadataMap: QueueItemMetadataMap,
): void => {
    const currentMap = get(queueMetadata);

    queueMetadata.set(new Map([...currentMap, ...queueMetadataMap]));
};
